import React, { useState } from 'react';
import { Box, Typography, Button, Card, CardActionArea, Alert, Stack, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink } from 'react-router-dom';
import { rebuildStats } from './statsService';

const REPORTS = [
  { to: '/kpis/yoy', title: 'Year on year', desc: 'Revenue / net sales / orders / units by week, month or quarter — years overlaid.' },
  { to: '/kpis/country', title: 'Sales by country', desc: 'Country × year — units or revenue, raw or %.' },
  { to: '/kpis/region', title: 'Sales by region', desc: 'UK / EU / US / RoW × year.' },
  { to: '/kpis/channel', title: 'Sales by channel', desc: 'Etsy / eBay / Shopify / Direct × year.' },
  { to: '/kpis/channel-yoy', title: 'Channel year on year', desc: 'Small multiples — each channel month-by-month, years overlaid.' },
  { to: '/kpis/channel-trend', title: 'Channel trend', desc: 'Continuous monthly stacked area or lines by channel.' },
];

/** KPIs landing: rebuild control + links to each report. */
export default function KpisPage() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  async function rebuild() {
    setBusy(true);
    setResult(null);
    setError(null);
    try {
      setResult(await rebuildStats());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>KPIs</Typography>
        <Button variant="outlined" startIcon={busy ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={rebuild} disabled={busy}>
          {busy ? 'Rebuilding…' : 'Rebuild stats'}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reports read pre-aggregated weekly/monthly summaries. Rebuild after importing to refresh them
        (also runs automatically after an import).
      </Typography>
      {result && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Stats rebuilt from {result.orders} orders — {result.months} months, {result.weeks} weeks.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>Rebuild failed: {error}</Alert>}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' } }}>
        {REPORTS.map((r) => (
          <Card key={r.to} variant="outlined">
            <CardActionArea component={RouterLink} to={r.to} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              <Typography variant="h6">{r.title}</Typography>
              <Typography variant="body2" color="text.secondary">{r.desc}</Typography>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
